import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslate } from 'react-admin';

export interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when user confirms the action */
  onConfirm: () => void | Promise<void>;
  /** Dialog title */
  title: string;
  /** Dialog message/description */
  message: string;
  /** Confirm button text (default: "Confirm") */
  confirmText?: string;
  /** Cancel button text (default: "Cancel") */
  cancelText?: string;
  /** Whether the confirm button should be styled as dangerous (red) */
  isDangerous?: boolean;
  /** Whether the action is in progress */
  isLoading?: boolean;
}

/**
 * ConfirmationDialog - Generic confirmation dialog
 *
 * Used for:
 * - Leave conversation confirmation
 * - Delete conversation confirmation
 * - Remove participant confirmation
 * - Archive conversation confirmation
 *
 * @example
 * ```tsx
 * <ConfirmationDialog
 *   open={showDeleteDialog}
 *   onClose={() => setShowDeleteDialog(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Conversation"
 *   message="Are you sure you want to delete this conversation? This action cannot be undone."
 *   confirmText="Delete"
 *   isDangerous
 *   isLoading={isDeleting}
 * />
 * ```
 */
export function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  isDangerous = false,
  isLoading = false,
}: ConfirmationDialogProps) {
  const translate = useTranslate();

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {cancelText || translate('ra.action.cancel')}
        </Button>
        <Button
          variant="contained"
          color={isDangerous ? 'error' : 'primary'}
          onClick={handleConfirm}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {confirmText || translate('ra.action.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmationDialog;
